import { stringifyError } from "./string";

export interface GuidLogging {
  crypto: boolean;
  perfNow: boolean;
  exceptions: string[];
}

type GuidTuple = [string, GuidLogging];

/**
 * Returns a tuple containing new random guid and an object for logging which specifies how that Guid was generated
 * RFC4122 http://www.ietf.org/rfc/rfc4122.txt section 4.4
 */
export function createNewGuid(): GuidTuple {
  const randomNumbers = new Uint8Array(31);
  const newGuidLogging = getRandomNumbers(randomNumbers);

  // RFC4122 http://www.ietf.org/rfc/rfc4122.txt
  const guidBase: string = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx";
  let i = 0;
  const guid = guidBase.replace(/[xy]/g, (c) => {
    const r = randomNumbers[i] & 0xf;
    i += 1;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
  return [guid, newGuidLogging];
}

function getRandomNumbers(arr: Uint8Array): GuidLogging {
  const newGuidLogging: GuidLogging = {
    crypto: false,
    perfNow: false,
    exceptions: [],
  };
  const crypto = window.crypto || window.msCrypto;
  if (crypto) {
    newGuidLogging.crypto = true;
    try {
      crypto.getRandomValues(arr);
      // Early return if we were able to use crypto
      return newGuidLogging;
    } catch (e: any) {
      // Do nothing we want to continue down to use Math.random()
      newGuidLogging.exceptions.push(`crypto: ${stringifyError(e)}`);
    }
  }
  newGuidLogging.perfNow = true;
  // Either crypto is not available or there was an exception, use Math.random() instaed
  for (let i = 0; i < arr.length; i += 1) {
    try {
      // We use performance.now() to increase the entropy for Math.random()
      // Specifically, looks like some scripted crawlers (device name shows as "Spider") have a problem where Math.random() isn't seeded properly
      // We do that by throwing away some numbers from Math.random().
      // This guarantees that the distribution of numbers will not be skewed (vs. using the performance.now() bits directly)

      // performance.now() is required to be accurate down to 5 micro seconds. So the least significant decimal digit is going to be either 0 or 5
      // That means we basically get one of two values, which is not enough. So we take the second least significant decimal digit instead.
      // That's why we multiply by 100 not 1000
      let n = Math.floor(window.performance.now() * 100) % 10;
      // Just for safety, make sure we're never out of bounds
      if (n < 0 || n > 9) {
        n = 0;
      }
      for (let j = 0; j < n; j += 1) {
        // tslint:disable-next-line:insecure-random
        Math.random();
      }
    } catch (e: any) {
      // Do nothing, protection against anything above going wrong
      newGuidLogging.exceptions.push(`Math.random: ${stringifyError(e)}`);
    }
    // tslint:disable-next-line:insecure-random
    arr[i] = Math.floor(Math.random() * 16);
  }
  return newGuidLogging;
}

export function generateRandomUuid(): string {
  return "xxxxxxxx-xxxx-4xxx-xxxx-xxxxxxxxxxxx".replace(
    /[x]/g,
    generateRandomHexChar
  );
}

function generateRandomHexChar(): string {
  const uint8Array: Uint8Array = new Uint8Array(1);
  if (window.self.crypto && window.self.crypto.getRandomValues) {
    window.self.crypto.getRandomValues(uint8Array);
    return Math.floor(uint8Array[0] / 16).toString(16);
  }

  if (window.msCrypto && window.msCrypto.getRandomValues) {
    window.msCrypto.getRandomValues(uint8Array);
    return Math.floor(uint8Array[0] / 16).toString(16);
  }

  // tslint:disable-next-line:insecure-random
  return Math.floor(Math.random() * 16).toString(16);
}
